/**
 * Every word the signer sees that isn't data from the server or the form pack.
 * One catalog, so another language is a second object of the same shape.
 * Functions take the names the server sends (sender, contact, signer) — never hard-coded people.
 */

export interface Who {
  /** Short sender name, e.g. "Golden Sparrow". */
  sender: string;
  /** Legal name, e.g. "Golden Sparrow LLC". */
  legal: string;
  /** Contact person's full name, e.g. "Neha Kapoor". */
  contact: string;
  /** Contact person's first name, e.g. "Neha". */
  contactFirst: string;
  contactEmail: string;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const S = {
  product: "gSignatures",
  securedBy: "Secured by gSignatures",
  steps: ["Verify", "Consent", "Letters", "Your details", "Review & sign"] as const,
  stepOf: (i: number, n: number) => `Step ${i} of ${n}`,
  progressNav: "Progress",

  top: {
    help: "Help",
    more: "More options",
    saved: "Saved",
    saving: "Saving…",
    retrying: "Couldn't save — retrying",
    failed: "Couldn't save",
    savedTitle: "Your answers are saved as you go",
    menuPaper: "Sign on paper instead",
    menuPaperSub: "Stop signing electronically. No fee, nothing else changes.",
    menuDecline: "Decline the offer",
    menuDeclineSub: (w: Who) => `Tell ${w.contactFirst} you're not taking it.`,
    menuDownload: "Download documents",
    menuDownloadSub: "The PDFs as they are now.",
  },

  common: {
    continue: "Continue",
    back: "Back",
    close: "Close",
    cancel: "Cancel",
    retry: "Try again",
    loading: "Loading…",
    optional: "Optional",
    lineTag: (line: string) => `Line ${line}`,
    lineTitle: (issuer: string) => `Line on the ${issuer} form`,
    copy: "Copy",
    copied: "Email address copied.",
    copyFailed: "Select the address to copy it.",
  },

  errors: {
    offline: "We couldn't reach gSignatures. Check your connection — your answers are kept on this page.",
    rateLimited: "That's a lot of requests in a short time. Wait a minute, then try again.",
    outOfDate: "This page was a step behind — we've caught up.",
    previewMismatch: "The document changed while you were signing. Check it, then sign again.",
    invalidValues: "A few answers need a fix — they're marked in red.",
    generic: "Something went wrong on our side. Try again in a moment.",
    sessionEnded: "For your security, we've locked this page. Confirm it's you to carry on.",
  },

  session: {
    endingSoon: "For your security, this page locks soon.",
    keepGoing: "Keep going",
  },

  dead: {
    title: "This link isn't working",
    text: "It may have been copied incompletely, or it's no longer active. If you were expecting documents, contact the person who sent them.",
  },

  verify: {
    title: "Confirm it's you",
    verified: "Thanks — you're verified",
    intro: (w: Who, masked: string) => ({ before: `${w.sender} sent you an offer letter and a tax form. To open them, we'll email a 6-digit code to `, email: masked, after: "." }),
    sendCode: "Email me a code",
    sending: "Sending…",
    sentTo: (masked: string) => ({ before: "We sent a 6-digit code to ", email: masked, after: ". Enter it to open your documents." }),
    codeLabel: "6-digit code",
    digit: (i: number) => `Digit ${i} of 6`,
    checking: "Checking…",
    wrong: (left: number) => `That code doesn't match. ${left} ${plural(left, "try", "tries")} left.`,
    expired: "That code has expired. Send yourself a new one.",
    demoHint: "Demo: any 6 digits work · 000000 is a wrong code",
    resendIn: (s: number) => `Send a new code in 0:${String(s).padStart(2, "0")}`,
    resend: "Send a new code",
    alreadySent: "We sent you a code a moment ago — check your email.",
    resent: "We've sent a new code.",
    notYou: (first: string) => `Not ${first}? Close this page — nothing happens unless you sign.`,
    footer: (w: Who, first: string) =>
      `Not ${first}? Close this page — nothing happens unless you sign. The code expires in 10 minutes. After 5 wrong codes we pause this link and tell ${w.contactFirst}.`,
  },

  consent: {
    eyebrow: "Before you start",
    lede: (w: Who) => `${w.sender} uses gSignatures to send these documents. Here's what signing electronically means for you.`,
    readFull: "Read the full disclosure",
    hideFull: "Hide the full disclosure",
    paper: "Sign on paper instead",
    paperShort: "On paper",
  },

  offer: {
    eyebrow: "Offer letter",
    titleAt: (role: string, sender: string) => `${role} at ${sender}`,
    lede: "Read the full letter. Here are the key terms.",
    termsLabel: "Key terms",
    companySigned: (w: Who) => `${w.sender} has already signed. You'll add your signature at the last step, after the tax form.`,
    companyCountersigns: (w: Who, name: string) => `You sign first; ${name} countersigns for ${w.sender} after you. You'll add your signature at the last step.`,
    mergedNote: (w: Who) => `Words with a blue underline were filled in for you by ${w.sender}.`,
    accept: "Accept and continue",
    acceptShort: "Accept",
    decline: "Decline this offer",
    declineShort: "Decline",
    viewerName: (title: string, sender: string) => `${title} · ${sender}`,
    filledBy: (w: Who) => `Filled in by ${w.sender}`,
    signLater: "You'll sign here at the last step",
    dateLater: "Date: filled in when you sign",
  },

  chooser: {
    eyebrow: "Tax form",
    title: "One question first",
    lede: (w: Who) => `${w.sender} is a U.S. company. The IRS needs to know whether you're a U.S. taxpayer, and the answer picks your form.`,
    whatCounts: "What counts as a U.S. resident for tax purposes?",
    unavailable: (w: Who) => `This form can't be filled in online yet. ${w.contact} will send it to you another way — write to`,
    change: "Change your answer",
  },

  upload: {
    eyebrow: "Your details",
    title: (what: string) => `Add a picture of your ${what}`,
    lede: (w: Who) => `${w.sender} keeps it with your signed documents. Only they can open it.`,
    takePhoto: "Take a photo",
    choosePicture: "Choose a picture",
    adding: "Adding…",
    remove: "Remove",
    removeLabel: (n: number) => `Remove picture ${n}`,
    pictureAlt: (n: number, what: string) => `Picture ${n} of your ${what}`,
    count: (n: number, max: number) => (max === 1 ? (n ? "Added" : "Not added yet") : `${n} of ${max} added`),
    tips: "Lay it flat in good light, with all four corners in the photo and the writing easy to read.",
    secondHint: "Add the back too if it has details on it.",
    needOne: "Add a picture to continue.",
    locked: "Signing has started, so the picture can't change now.",
    problem: {
      not_image: "That file isn't a picture. Choose a photo (JPEG or PNG).",
      too_large: "That picture is too large even after shrinking it. Try a photo from your camera.",
      unreadable: "That picture couldn't be read. Try taking the photo again.",
    } as Record<"not_image" | "too_large" | "unreadable", string>,
  },
  tax: {
    eyebrowSuffix: "IRS",
    title: "Your tax details",
    lede: (w: Who) => `This form tells ${w.sender} you're not a U.S. taxpayer, so no U.S. tax is taken from your stipend. Your answers fill in the official form as you type.`,
    progressLabel: "Required answers done",
    progressCount: (done: number, total: number) => `${done} of ${total}`,
    seeOnForm: "See it on the form",
    nextUnanswered: "Next unanswered",
    formShort: "Form",
    sectionDone: "Done",
    sectionCount: (done: number, total: number) => `${done} of ${total}`,
    review: "Review answers",
    reviewShort: "Review",
    needsFix: "A few answers need a fix — they're marked in red.",
    chooseCountry: "Choose a country",
    choose: "Choose…",
    live: "Live",
    sheetTitle: (title: string) => `${title}, as filled in`,
    hintsLabel: "Format",
    hintDone: "done",
    hintTodo: "not yet",
    ghostSignature: "Your signature goes here",
    ghostDate: "Filled in when you sign",
    signHere: "Sign here",
    lineChip: (line: string) => `Line ${line}`,
    revision: (rev: string) => {
      const m = /^(\d{4})-(\d{2})$/.exec(rev);
      if (!m) return `Rev. ${rev}`;
      const month = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)).toLocaleString("en-US", { month: "long", timeZone: "UTC" });
      return `Rev. ${month} ${m[1]}`;
    },
  },

  review: {
    eyebrow: "Review",
    title: "Check everything before you sign",
    lede: "You can still change anything. Signing is the last step.",
    view: "View",
    edit: "Edit",
    editLabel: (what: string) => `Edit ${what}`,
    pages: (n: number) => `${n} ${plural(n, "page", "pages")}`,
    validThrough: (date: string) => `Valid through ${date}, unless your details change.`,
    warningTail: (w: Who) => `You can still sign — ${w.sender} will see this note.`,
    blank: "Left blank",
    yes: "Yes",
    no: "No",
    fingerprint: "Each document gets a fingerprint (SHA-256) when you sign, so anyone can check it hasn't changed since.",
    continue: "Continue to sign",
  },

  sign: {
    eyebrow: "Sign",
    title: "Two signatures, and you're done",
    lede: "The offer letter, then the tax form. Each is its own signature.",
    create: "Create your signature",
    change: "Change",
    yourSignature: "Your signature",
    /** "Sign the offer letter", "Sign the agreement". */
    letterAct: (title: string) => `Sign the ${title.toLowerCase()}`,
    letterStatement: (title: string, version: string, legal: string) => ({ before: "By selecting ", button: `Sign ${title.toLowerCase()}`, after: `, I adopt the signature above and intend to sign the ${title} (${version}) from ${legal}.` }),
    letterButton: (title: string) => `Sign ${title.toLowerCase()}`,
    act2: (title: string) => `Sign ${title}`,
    act2Button: (title: string) => `Sign ${title}`,
    signing: "Signing…",
    signedAt: (time: string) => `Signed ${time}`,
    createFirst: "Create your signature first.",
    certLabel: "The certification you're signing",
    scrollHint: "Scroll to the end to sign",
    readAll: "You've read the certification",
    backToReview: "Back to review",
    seeExact: "See the exact PDF you're signing",
    fingerprint: (hash: string) => `Fingerprint ${hash}`,
    eSigned: (at: string) => `E-signed ${at}`,
    /** "Sign the offer letter first." / "Sign the offer letter and the agreement first." */
    signFirst: (titles: string[]) => `Sign the ${titles.map((t) => t.toLowerCase()).join(" and the ")} first.`,
    viewerLetter: (title: string) => `${title}`,
  },

  adopt: {
    title: "Create your signature",
    tabs: { type: "Type", draw: "Draw", upload: "Upload" },
    tabsLabel: "How to make your signature",
    name: "Your name",
    style: "Signature style",
    drawLabel: "Draw your signature",
    drawHint: "Draw your signature here",
    drawHelp: "Use your finger, a stylus or a mouse.",
    undo: "Undo",
    clear: "Clear",
    tooSmall: "That's a little small — sign across more of the box.",
    upload: "Choose a photo of your signature",
    uploadHelp: "PNG or JPG, on white paper",
    uploadAlt: "Your uploaded signature",
    uploadTooBig: "Choose an image under 5 MB.",
    uploadType: "Choose a PNG or JPG image.",
    replace: "Choose a different photo",
    agree: { before: "By selecting ", button: "Adopt signature", after: ", I agree this mark is my electronic signature, and that it means the same as my handwritten signature on the documents I sign with it." },
    adopt: "Adopt signature",
    adopting: "Saving…",
    adopted: "Signature adopted.",
    alt: "Your signature",
  },

  done: {
    title: (first: string) => `You're all set, ${first}.`,
    lede: (when: string, w: Who) => `Signed ${when}. ${w.sender} has been told.`,
    download: "Download",
    downloadAll: "Download all",
    letterSub: (w: Who) => `Signed by you and ${w.sender}`,
    countersignNext: (w: Who) => `${w.sender} countersigns next. Your signed copies are emailed to you as soon as they have — you can also download them here then.`,
    uploadSub: (n: number) => (n === 1 ? "The picture you added" : `The ${n} pictures you added`),
    formSub: (date: string) => `Valid through ${date}`,
    certNote: "Each PDF ends with a certificate page: who signed, when and how — every step.",
    emailNote: (w: Who, formTitle: string) =>
      `We've emailed you the signed offer letter. Your ${formTitle} isn't emailed because it has your tax ID — download it here; this page keeps it for 30 days, and ${w.contactFirst} can send you a copy any time.`,
    nextTitle: "What happens next",
    next1: (w: Who, formTitle: string) => `${w.sender} checks your ${formTitle} — usually the same day.`,
    next2: (w: Who) => `${w.contact} will be in touch before you start.`,
    trust: "Signed securely with gSignatures. Every step is recorded with the time, IP address and device.",
  },

  states: {
    expired: {
      title: "This link has expired",
      text: (w: Who) => `Links to these documents work for 14 days. Ask ${w.contact} for a new one — your answers so far are saved.`,
      cta: "Ask for a new link",
      mailSubject: "A new signing link, please",
    },
    paused: {
      title: "We've paused this link",
      text: (w: Who) => `Too many wrong codes were entered. To keep your documents safe, we've paused this link and told ${w.contact}, who can send you a fresh one.`,
      cta: (w: Who) => `Email ${w.contactFirst}`,
    },
    completed: {
      title: "You've already signed these documents",
      text: (when: string | null) => (when ? `You signed on ${when}. Confirm it's you to download your copies.` : "Confirm it's you to download your copies."),
      cta: "Get my copies",
    },
    voided: {
      title: (w: Who) => `${w.sender} withdrew these documents`,
      text: (w: Who) => `${w.contact} withdrew this request. There's nothing more to do. Questions? Write to ${w.contactEmail}.`,
    },
    declined: {
      title: "You declined this offer",
      text: (w: Who) => `We've told ${w.contact}. If that was a mistake, reply to the email and they can send the offer again.`,
    },
    paper: {
      title: "You've chosen to sign on paper",
      text: (w: Who) =>
        `Here's how: download the packet (the offer letter, already signed by ${w.sender}, and a blank tax form), print and sign each page in ink, then email a photo or scan of every page to ${w.contactFirst}.`,
      cta: "Download the packet",
      verifyCta: "Confirm it's you to download",
      email: (w: Who) => `Email the pages to ${w.contactFirst}`,
      mailSubject: "My signed pages",
    },
  },

  modals: {
    helpTitle: "Need help?",
    helpIntro: (w: Who) => `${w.contact} at ${w.sender} sent you these documents and can answer questions about the offer or the form.`,
    faq: [
      { q: "Can I finish later?", a: () => "Yes. Your answers are saved as you go. Open the same link again and enter a new code." },
      { q: "Why Form W-8BEN?", a: (w: Who) => `${w.sender} is a U.S. company. This IRS form shows you're not a U.S. taxpayer, so no U.S. tax is taken from your stipend.` },
      { q: "Is this secure?", a: () => "Your documents are encrypted and only you can open this link, with a code sent to your email. Signed documents are sealed so any later change shows." },
    ],
    paperTitle: "Sign on paper instead?",
    paperText: "You'll print the documents, sign them in ink, and send back a photo or scan. There's no fee, and the offer stays the same.",
    paperKeep: "Keep signing online",
    paperGo: "Switch to paper",
    declineTitle: "Decline this offer?",
    declineText: (w: Who) => `We'll tell ${w.contact}. You won't be able to sign this offer afterwards.`,
    declineReason: "Reason",
    declineReasonHint: (w: Who) => `Optional — shared with ${w.contactFirst}`,
    declinePlaceholder: "For example: I've accepted another role",
    declineBack: "Go back",
    declineGo: "Decline offer",
    downloadTitle: "Download documents",
    downloadText: "You'll get the PDFs as they are now — not yet signed by you.",
    blankSuffix: "(blank)",
  },

  viewer: {
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    download: "Download",
    pages: (n: number) => `${n} ${plural(n, "page", "pages")}`,
    pageLabel: (title: string, i: number, n: number) => `${title}, page ${i} of ${n}`,
    loading: "Opening the document…",
    failed: "We couldn't show this document.",
    placeholderTitle: "Document preview",
    placeholderDemo: (path: string) => `The demo file ${path} hasn't been added yet. Everything else on this page works.`,
  },
} as const;
